import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

type SessionInfo = {
  id: string;
  access: string;
  sessionFormType: string;
};

type AppContextProps = {
  currentPageId: string | undefined;
  setCurrentPageId: (pageIndex: string, scroll?: boolean) => void;
  completedNavigationItemsIds: string[];
  setCompletedNavigationItemsIds: (ids: string[]) => void;
  sessionInfo: SessionInfo;
  currentRoute: string;
};

export const AppContext = createContext<AppContextProps>({
  currentPageId: undefined,
  setCurrentPageId: () => '',
  completedNavigationItemsIds: [],
  setCompletedNavigationItemsIds: () => '',
  sessionInfo: { id: '', access: '', sessionFormType: '' },
  currentRoute: 'landing-page',
});

export const AppContextProvider = ({
  children,
  sessionInfo,
}: {
  children: ReactNode;
  sessionInfo: SessionInfo;
}) => {
  const router = useRouter();

  const [currentPageId, setCurrentPageId] = useState<string | undefined>();
  const [completedNavigationItemsIds, setCompletedNavigationItemsIds] =
    useState<string[]>([]);

  const setCurrentPageIdAndRoute = useCallback(
    (id: string, scroll: boolean = true) => {
      if (id && id !== currentPageId) {
        document.querySelector('main')?.scrollTo(0, 0);
        setCurrentPageId(id);
        // only push to the router if the id is different from the current query to avoid unnecessary route changes
        if (id !== router.query.page) {
          router.push({ query: { ...router.query, page: id } }, undefined, {
            scroll,
          });
        }
      }
    },
    [router, currentPageId],
  );

  const currentRoute =
    router.pathname === '/form'
      ? currentPageId || 'landing-page'
      : router.pathname.split('/').pop() || '';

  return (
    <AppContext.Provider
      value={{
        currentPageId,
        setCurrentPageId: setCurrentPageIdAndRoute,
        completedNavigationItemsIds,
        setCompletedNavigationItemsIds,
        sessionInfo,
        currentRoute,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = (): AppContextProps => useContext(AppContext);
